
import { calculateSeasonality, detectAnomalies } from './helpers';
import type { RevenueForecasting } from './types';

/**
 * AI-powered revenue forecasting using trend analysis and seasonality
 */
export function aiRevenueForecasting(
  historicalRevenue: number[],
  assumptions: any,
  forecastPeriods: number = 12
): RevenueForecasting {
  if (historicalRevenue.length < 3) {
    // Use simple growth model for limited data
    const baseRevenue = historicalRevenue[historicalRevenue.length - 1] || 0;
    const growthRate = assumptions.profitLoss?.revenueGrowthRate || 10;
    
    const forecast = Array.from({ length: forecastPeriods }, (_, i) => {
      return baseRevenue * Math.pow(1 + growthRate / 100, i + 1);
    });
    
    return {
      forecast,
      confidence: 0.6, // Lower confidence due to limited data
      trend: 'linear',
      seasonality: Array(Math.min(forecastPeriods, 12)).fill(1), // Default to no seasonality
      anomalies: [] // No anomalies detected with limited data
    };
  }

  // Calculate moving averages and trend analysis
  const periods = Math.min(historicalRevenue.length, 12);
  const recentData = historicalRevenue.slice(-periods);
  
  // Simple linear regression for trend
  const n = recentData.length;
  const x = Array.from({ length: n }, (_, i) => i + 1);
  const y = recentData;
  
  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((acc, xi, i) => acc + xi * y[i], 0);
  const sumXX = x.reduce((acc, xi) => acc + xi * xi, 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Apply seasonality and market adjustments
  const seasonalityFactor = assumptions.budget?.seasonalityFactor || 1;
  const marketAdjustment = 1 + (assumptions.general?.inflationRate || 0) / 100;
  
  const forecast = Array.from({ length: forecastPeriods }, (_, i) => {
    const baseValue = slope * (n + i + 1) + intercept;
    const seasonal = baseValue * (1 + Math.sin((i % 12) * Math.PI / 6) * (seasonalityFactor - 1));
    return Math.max(0, seasonal * Math.pow(marketAdjustment, i + 1));
  });
  
  // Calculate seasonality pattern
  const seasonality = calculateSeasonality(historicalRevenue, Math.min(12, historicalRevenue.length));
  
  // Detect anomalies
  const anomalies = detectAnomalies(historicalRevenue);
  
  // Calculate confidence based on data quality and trend stability
  const variance = recentData.reduce((acc, val, i) => {
    const predicted = slope * (i + 1) + intercept;
    return acc + Math.pow(val - predicted, 2);
  }, 0) / n;
  
  const confidence = Math.max(0.3, Math.min(0.95, 1 - (variance / (sumY / n))));
  
  const trendDirection = slope > 0 ? 'growing' : slope < 0 ? 'declining' : 'stable';
  
  return {
    forecast,
    confidence,
    trend: trendDirection,
    seasonality,
    anomalies
  };
}

/**
 * Advanced Time Series Analysis with ARIMA-like modeling
 */
export function advancedTimeSeriesForecasting(
  historicalData: number[],
  assumptions: any,
  forecastPeriods: number = 12
): RevenueForecasting {
  if (historicalData.length < 6) {
    return aiRevenueForecasting(historicalData, assumptions, forecastPeriods);
  }

  // Advanced moving average with exponential smoothing
  const alpha = 0.3; // Smoothing parameter
  const beta = 0.2;  // Trend parameter
  const gamma = 0.1; // Seasonality parameter
  
  // Calculate trend component
  const trends = [];
  for (let i = 1; i < historicalData.length; i++) {
    trends.push(historicalData[i] - historicalData[i - 1]);
  }
  
  // Detect seasonality patterns
  const seasonalPeriod = Math.min(12, Math.floor(historicalData.length / 2));
  const seasonality = calculateSeasonality(historicalData, seasonalPeriod);
  
  // Detect anomalies using statistical methods
  const anomalies = detectAnomalies(historicalData);
  
  // Triple exponential smoothing (Holt-Winters)
  let level = historicalData[0];
  let trend = trends.length > 0 ? trends[0] : 0;
  const seasonalIndices = Array(seasonalPeriod).fill(1);
  
  // Initialize seasonal indices
  if (historicalData.length >= seasonalPeriod) {
    for (let i = 0; i < seasonalPeriod; i++) {
      let sum = 0;
      let count = 0;
      for (let j = i; j < historicalData.length; j += seasonalPeriod) {
        sum += historicalData[j] / level;
        count++;
      }
      seasonalIndices[i] = count > 0 ? sum / count : 1;
    }
  }
  
  // Generate forecast
  const forecast = [];
  for (let i = 0; i < forecastPeriods; i++) {
    const seasonalIndex = seasonalIndices[i % seasonalPeriod];
    const forecastValue = (level + trend * (i + 1)) * seasonalIndex;
    forecast.push(Math.max(0, forecastValue));
  }
  
  // Calculate confidence based on model fit
  const residuals = historicalData.slice(1).map((actual, i) => {
    const predicted = level + trend * (i + 1);
    return Math.abs(actual - predicted);
  });
  
  const mape = residuals.reduce((sum, residual, i) => 
    sum + Math.abs(residual / historicalData[i + 1]), 0) / residuals.length;
  
  const confidence = Math.max(0.3, Math.min(0.95, 1 - mape));
  const trendDirection = trend > 0 ? 'growing' : trend < 0 ? 'declining' : 'stable';
  
  return {
    forecast,
    confidence,
    trend: trendDirection,
    seasonality,
    anomalies
  };
}
